import React, { useState, useEffect, useRef, useCallback } from 'react'
import {
  formatDate,
  weeks,
  getAddDay
} from '@/feature/phr-28/components/template/mastaRegistration/shiftRegistration/DailyCalendar/CalendarCommon'
import {
  type daysType,
  type HolidaysType,
  type MonthCalendarType
} from '@/feature/phr-28/components/common/type'
import { useNavigate } from '@tanstack/react-location'
import { getUnixTime, startOfDay } from 'date-fns'

function classNames (...classes: Array<string | boolean | undefined>): string {
  return classes.filter(Boolean).join(' ')
}

function getIsHoliday (timestamp: Date, holidays: HolidaysType | undefined): boolean {
  if (holidays === undefined) return false;

  const holiday = holidays[getUnixTime(startOfDay(timestamp))]
  const isHoliday = holiday !== undefined
  return isHoliday;
}

// 先月の日付データを作成
function createPreviousMonthDay (
  year: number, 
  lastMonth: number, 
  weekLine: number, 
  dayBox: number, 
  startDay: number, 
  lastMonthendDayCount: number
): daysType {
  const num = lastMonthendDayCount - startDay + dayBox + 1
  return {
    date: `${year}/${lastMonth}/${num}`,
    isDisabled: true,
    isCurrentMonth: false,
    isHoliday: false
  }
}

// 来月の日付データを作成
function createNextMonthDay (
  year: number, 
  nextMonth: number, 
  dayCount: number, 
  endDayCount: number
): daysType {
  const num = dayCount - endDayCount
  return {
    date: `${year}/${nextMonth}/${num}`,
    isDisabled: true,
    isCurrentMonth: false,
    isHoliday: false
  }
}

// 今月の日付データ作成用パラメータの型定義
interface CurrentMonthDayParams {
  date: {
    year: number
    month: number
    dayCount: number
    nowDate: Date
  }
  settings: {
    reservationNotPossibleDays: number
    regularHoliday: number
    holidays: HolidaysType | undefined
  }
}

// 今月の日付データを作成
function createCurrentMonthDay(params: CurrentMonthDayParams): daysType {
  const { date, settings } = params
  const { year, month, dayCount, nowDate } = date
  const { reservationNotPossibleDays, regularHoliday, holidays } = settings
  
  const roopDate: Date = new Date(`${year}/${month}/${dayCount}`)
  return {
    date: `${year}/${month}/${dayCount}`,
    isDisabled:
      getAddDay(new Date(), reservationNotPossibleDays) < roopDate
        ? roopDate.getDay() === regularHoliday
        : true,
    isCurrentMonth: true,
    isSelected: new Date(nowDate).getDate() === dayCount,
    isToday: formatDate(new Date()) === formatDate(roopDate),
    isHoliday: roopDate.getDay() === 0 || getIsHoliday(roopDate, holidays)
  }
}

// カレンダーデータ作成関数
function createCalendarDays (nowDate: Date, holidays: HolidaysType | undefined): daysType[] {
  const date = nowDate
  const year = date.getFullYear()
  const month = date.getMonth() + 1
  const lastMonth = date.getMonth()
  const nextMonth = date.getMonth() + 2
  const startDate = new Date(year, month - 1, 1) // 月の最初の日を取得
  const endDate = new Date(year, month, 0) // 月の最後の日を取得
  const endDayCount = endDate.getDate() // 月の末日
  const lastMonthEndDate = new Date(year, month - 1, 0) // 前月の最後の日の情報
  const lastMonthendDayCount = lastMonthEndDate.getDate() // 前月の末日
  const startDay = startDate.getDay() // 月の最初の日の曜日を取得
  // 予約不可日数と定休日をoffにする値
  const limitCancellationNum: number = -1
  const reservationNotPossibleDays: number = limitCancellationNum
  const regularHoliday: number = limitCancellationNum
  let dayCount = 1 // 日にちのカウント

  // week分ループ
  const days: daysType[] = []
  for (let weekLine = 0; weekLine < 6; weekLine++) {
    for (let dayBox = 0; dayBox < 7; dayBox++) {
      // 先月の日程
      if (weekLine === 0 && dayBox < startDay) {
        days.push(createPreviousMonthDay(year, lastMonth, weekLine, dayBox, startDay, lastMonthendDayCount))
      } else if (dayCount > endDayCount) {
        // 来月の日程
        days.push(createNextMonthDay(year, nextMonth, dayCount, endDayCount))
        dayCount++
      } else {
        // 今月の日程
        days.push(createCurrentMonthDay({
          date: { year, month, dayCount, nowDate },
          settings: { reservationNotPossibleDays, regularHoliday, holidays }
        }))
        dayCount++
      }
    }
  }

  return days
}

// 曜日ヘッダーコンポーネント
function WeekdayHeader (): JSX.Element {
  return (
    <div className="mt-1 grid grid-cols-7 text-center text-xs leading-6 text-gray-500">
      <div>日</div>
      <div>月</div>
      <div>火</div>
      <div>水</div>
      <div>木</div>
      <div>金</div>
      <div>土</div>
    </div>
  )
}

// 日付ボタンのクラス名を計算する関数
function getDayButtonClassName (day: daysType, dayIdx: number, daysLength: number): string {
  return classNames(
    'py-0.5 hover:bg-gray-100 focus:z-10',
    day.isDisabled && day.isCurrentMonth === false ? 'bg-gray-100' : day.isHoliday !== undefined && day.isHoliday ? 'bg-red-200' : 'bg-white',
    ((day.isSelected ?? false) || (day.isToday ?? false)) && 'font-semibold',
    (day.isSelected ?? false) && 'text-white',
    (day.isSelected === undefined || !day.isSelected) &&
    (day.isCurrentMonth ?? false) &&
    (day.isToday === undefined || !day.isToday) &&
    'text-gray-900',
    (day.isSelected === undefined || !day.isSelected) &&
    (day.isCurrentMonth === undefined || !day.isCurrentMonth) &&
    (day.isToday === undefined || !day.isToday) &&
    'text-gray-400',
    (day.isToday ?? false) && (day.isSelected === undefined || !day.isSelected) && 'text-indigo-600',
    dayIdx === 0 && 'rounded-tl-lg',
    dayIdx === 6 && 'rounded-tr-lg',
    dayIdx === daysLength - 7 && 'rounded-bl-lg',
    dayIdx === daysLength - 1 && 'rounded-br-lg'
  )
}

// 日付の時間表示のクラス名を計算する関数
function getTimeClassName (day: daysType): string {
  return classNames(
    'mx-auto flex h-7 w-7 items-center justify-center rounded-full',
    (day.isSelected ?? false) && (day.isToday ?? false) && 'bg-indigo-600',
    (day.isSelected ?? false) && day.isToday !== undefined && !day.isToday && 'bg-gray-900'
  )
}

// カレンダーグリッドコンポーネント
function CalendarGrid ({ days, setDays, setNowDate }: { 
  days: daysType[] | undefined, 
  setDays: React.Dispatch<React.SetStateAction<daysType[] | undefined>>,
  setNowDate: (date: Date) => void
}): JSX.Element {
  return (
    <div className="isolate mt-1 grid grid-cols-7 gap-px rounded-lg bg-gray-200 text-sm shadow ring-1 ring-gray-200">
      {days?.map((day, dayIdx) => (
        <button
          onClick={(e) => {
            if (day.isCurrentMonth ?? false) {
              setDays([])
              setNowDate(new Date(e.currentTarget.value))
            }
          }}
          value={day.date}
          key={day.date}
          type="button"
          className={getDayButtonClassName(day, dayIdx, days.length)}
        >
          <time
            dateTime={day.date}
            className={getTimeClassName(day)}
          >
            {day.date.split('/').pop()?.replace(/^0/, '')}
          </time>
        </button>
      ))}
    </div>
  )
}

// カレンダーナビゲーションコンポーネント
function CalendarNavigation ({ nowDate, calcMonth }: { 
  nowDate: Date, 
  calcMonth: (value: number) => void 
}): JSX.Element {
  return (
    <div className="grid grid-cols-5 items-center text-center text-gray-900">
      <button
        onClick={() => { calcMonth(-1); }}
        type="button"
        className="text-white bg-gray-700 hover:bg-gray-800 font-medium rounded-l text-[0.7rem] h-7 text-center dark:bg-gray-600 dark:hover:bg-gray-700 dark:focus:ring-blue-800"
      >
        先月
      </button>
      <div className="col-span-3 text-[0.7rem] flex-auto font-semibold leading-3">
        <span>{`${nowDate.getFullYear()}`}</span>
        <br />
        <span>{`${new Date(nowDate).getMonth() + 1}月${new Date(nowDate).getDate()}日(${weeks[nowDate.getDay()]})`}</span>
      </div>
      <button
        onClick={() => { calcMonth(1); }}
        type="button"
        className="text-white bg-gray-700 hover:bg-gray-800 font-medium rounded-r text-[0.7rem] h-7 text-center dark:bg-gray-600 dark:hover:bg-gray-700 dark:focus:ring-blue-800"
      >
        翌月
      </button>
    </div>
  )
}

// 日付コントロールコンポーネント
function DateControls ({ calcDate, setDays, setNowDate }: {
  calcDate: (value: number) => void,
  setDays: React.Dispatch<React.SetStateAction<daysType[] | undefined>>,
  setNowDate: (date: Date) => void
}): JSX.Element {
  return (
    <div className="flex flex-row justify-between">
      <div className="relative z-0 shadow-sm rounded-md w-full grid grid-cols-6 gap-0.5 h-8 mt-3">
        <button
          onClick={() => { calcDate(-7); }}
          type="button"
          className="text-white bg-gray-700 hover:bg-gray-800 font-medium rounded-l text-[0.7rem] text-center dark:bg-gray-600 dark:hover:bg-gray-700 dark:focus:ring-blue-800"
        >
          先週
        </button>
        <button
          onClick={() => { calcDate(-1); }}
          type="button"
          className="text-white bg-gray-700 hover:bg-gray-800 font-medium text-[0.7rem] text-center dark:bg-gray-600 dark:hover:bg-gray-700 dark:focus:ring-blue-800"
        >
          前日
        </button>
        <button
          onClick={() => {
            setDays([])
            setNowDate(new Date())
          }}
          type="button"
          className="col-span-2 text-white bg-indigo-600 hover:bg-indigo-700 font-medium text-[0.7rem] text-center dark:bg-gray-600 dark:hover:bg-gray-700 dark:focus:ring-blue-800"
        >
          当日
        </button>
        <button
          onClick={() => { calcDate(1); }}
          type="button"
          className="text-white bg-gray-700 hover:bg-gray-800 font-medium text-[0.7rem] text-center dark:bg-gray-600 dark:hover:bg-gray-700 dark:focus:ring-blue-800"
        >
          翌日
        </button>
        <button
          onClick={() => { calcDate(7); }}
          type="button"
          className="text-white bg-gray-700 hover:bg-gray-800 font-medium rounded-r text-[0.7rem] text-center dark:bg-gray-600 dark:hover:bg-gray-700 dark:focus:ring-blue-800"
        >
          翌週
        </button>
      </div>
    </div>
  )
}

export function MonthCalendar ({ nowDate, setNowDate, holidays }: MonthCalendarType): JSX.Element {
  const container = useRef<HTMLDivElement>(null)
  const containerNav = useRef<HTMLDivElement>(null)
  const containerOffset = useRef<HTMLDivElement>(null)
  const [days, setDays] = useState<daysType[]>()
  const navigate = useNavigate();

  useEffect(() => {
    if ((container.current != null) && (containerNav.current != null) && (containerOffset.current != null)) {
      // Set the container scroll position based on the current time.
      const currentMinute = new Date().getHours() * 60
      container.current.scrollTop =
        ((container.current.scrollHeight -
          containerNav.current.offsetHeight -
          containerOffset.current.offsetHeight) *
          currentMinute) /
        1440
    }
  }, [])

  // 日付移動
  function calcDate (value: number): void {
    // 新しいDateオブジェクトを作成して日付を変更
    const newDate = new Date(nowDate);
    newDate.setDate(newDate.getDate() + value);
    setDays([])
    setNowDate(newDate)
  }

  // 月移動
  const calcMonth = useCallback((value: number): void => {
    // 明示的に年と月を計算
    let year = nowDate.getFullYear();
    let month = nowDate.getMonth() + value;
    
    // 月が0未満または11を超える場合に年を調整
    if (month < 0) {
      const yearsToSubtract = Math.floor((Math.abs(month) + 11) / 12);
      year -= yearsToSubtract;
      month = 12 - (Math.abs(month) % 12);
      if (month === 12) month = 0;
    } else if (month > 11) {
      const yearsToAdd = Math.floor(month / 12);
      year += yearsToAdd;
      month = month % 12;
    }
    
    // 常に1日を指定して新しい日付オブジェクトを作成
    const newDate = new Date(year, month, 1);
    
    // デバッグ情報
    console.log('元の日付:', nowDate);
    console.log('調整後の年月:', year, month);
    console.log('新しい日付:', newDate);
    
    setDays([]);
    setNowDate(newDate);
  }, [nowDate, setNowDate]);

  useEffect(() => {
    // date更新の度にカレンダー修正
    const newDays = createCalendarDays(nowDate, holidays)
    setDays(newDays)
    navigate({ to: `?calendar-date=${getUnixTime(nowDate)}` })
  }, [nowDate, holidays, navigate])

  return (
    <>
      <div className="hidden max-w-md flex-none md:block px-1">
        <CalendarNavigation nowDate={nowDate} calcMonth={calcMonth} />
        <WeekdayHeader />
        <CalendarGrid days={days} setDays={setDays} setNowDate={setNowDate} />
        <DateControls calcDate={calcDate} setDays={setDays} setNowDate={setNowDate} />
      </div>
    </>
  )
}
